<template>
  <div class="content sub" :class="{ 'gnb_hidden': !showNavbar }">
    <div class="gage_bar">
      <span :style="{ width: progressBarWidth + '%' }" />
    </div>
    <div class="inner_box">
      <div class="detail_top">
        <div class="img">
          <img v-if="post" :src="post.thumbnailImageUrl" alt="">
        </div>
        <div v-if="post" class="text_box">
          <div class="text">
            <p class="font_p3">
              {{ post.categoryName }} <span>{{ post.interestName }}</span> <span>{{ convertDate(post.creDt) }}</span>
            </p>
            <h2 class="font_h3">
              {{ post.title }}
            </h2>
          </div>

          <div v-if="post && business" class="link_company_follow">
            <a href="#" @click.prevent="navigateToBusiness(business.businessId, { query: {post: postSeq } })">
              <span class="logo">
                <img :src="thumbnailImageWithOptions(business.logoImageId ?? '', { w: 24 })" alt="">
              </span>
              <span class="name font_h6">{{ business.companyName }}</span>
            </a>

            <button-follow
              :id="business.businessSeq"
              class="small fc-white detail"
              style="margin-left:8px;"
              :follow-yn="business.followYn"
              :post-seq="post?.postSeq"
              @update="handleFollow"
            />
          </div>
        </div>
      </div>

      <div id="render_data" />
      <tms-render-editor-shadow
        class="detail_data"
        :class="{ logout: !state.isAuth && (post?.categoryNoDepth1 == 137623 || post?.categoryNoDepth1 == 234793) }"
        :dom="detail"
        style="max-width:100%"
      />

      <div v-if="!state.isAuth && (post?.categoryNoDepth1 == 137623 || post?.categoryNoDepth1 == 234793)" class="banner_join">
        <div class="gradient" />
        <div class="data">
          <p class="font_h4">
            인사이트가 담긴 뒷 이야기가 궁금하다면
          </p>
          <p class="font_p2">
            지금 로그인하고 모든 콘텐츠를 살펴보세요!
          </p>
          <div class="btn_center mt60">
            <button type="button" class="btn_ty_01" @click="navigateToJoin()">
              회원가입
            </button>
            <button type="button" class="btn_ty_01" @click="navigateToLogin()">
              로그인하기
            </button>
          </div>
        </div>
      </div>

      <div v-if="post" class="banner_tag">
        <h2 class="font_h4">
          Tag
        </h2>
        <div class="txt_list">
          <ul>
            <li v-for="tag in convertCommaToList(post.tags)" :key="tag">
              <a href="#" @click.prevent="navigateToSearchContents(tag, SEARCH_AFFECT_TYPE.Related)">#{{ tag }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="post && business" class="banner_company">
        <div class="link_company_text">
          <a href="#" @click.prevent="">
            <span class="logo">
              <span />
              <img :src="thumbnailImageWithOptions(business.logoImageId, { w: 58 })" alt="">
            </span>
            <span class="box">
              <span class="name font_h5">{{ business.companyName }}</span>
              <span class="text font_p3">{{ business.locationName }} <span>{{ business.sizeName }}</span></span>
              <span class="num font_h7">콘텐츠 {{ convertComma(business.contentsCnt) }} <span>팔로워 {{
                convertComma(business.followerCnt) }}</span></span>
            </span>
          </a>
        </div>

        <div class="btn_box num2">
          <button-follow
            :id="business.businessSeq"
            :follow-yn="business.followYn"
            :post-seq="post?.postSeq"
            @update="handleFollow"
          />
          <a
            href="#"
            class="btn_ty_01 small"
            @click.prevent="navigateToBusiness(business.businessId, { query: {post: postSeq } })"
          >
            기업 페이지로 이동하기
          </a>
        </div>
        <p class="font_p2">
          {{ business.introduceContents }}
        </p>
      </div>
    </div>

    <div v-if="post" class="popup_bookmar">
      <p class="font_p2">
        콘텐츠가 마음에 든다면 <span>관심목록에서 모아볼 수 있게</span> 북마크해보세요!
      </p>
      <button-bookmark :id="post.postSeq" class="white" :bookmark-yn="post.likeYn" @update="handleUpdateBookmark" />
      <div class="link_share">
        <button type="button" @click="handleClickShare()">
          <i class="icon_size_24 icon_font_share" />
          <span class="blind">공유하기</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DateUtil } from '@tms/utils'
import { SigninAuth } from '@tms/authorization'

import type { IBusiness, IPost } from '~/modules/content/types/model'
import useContentNavigator from '~/modules/content/composables/navigate'

import PATH from '~/modules/content/config/content-path'
import useContentInterest from '~/modules/content/composables/interest'
import { convertCommaToList, convertComma } from '~/utils/convert'

import { useMemberNavigator } from '~/modules/member/common/composables/navigator'
import { useImagesThumbnail } from '~/composables/images/thumbnail'
import { SEARCH_AFFECT_TYPE, AFFECT_TYPE } from '~/modules/content/types/type'
import type { IPostReponse } from '~/modules/content/types/response'
import useContentTracking from '~/modules/content/composables/tracking'

definePageMeta({
  key: route => route.fullPath,
  keepalive: true
})

const auth = new SigninAuth()

const route = useRoute()
const { navigateToSearchContents, navigateToBusiness } = useContentNavigator()
const { trackingByViewContent, trackingByFollowContent, trackingByBookmarkContent, trackingByShareContent } = useContentTracking()
const { readByPost, share, shareByAnalytics } = useContentInterest()
const { navigateToLogin, navigateToJoin } = useMemberNavigator()
const { thumbnailImageWithOptions } = useImagesThumbnail()

const tracking = reactive({ isBookmark: false, isFollow: false })
const state = reactive<{ isAuth: Boolean, isRead: Boolean }>({ isAuth: true, isRead: false })
const post = ref<IPost>()
const business = ref<IBusiness>()

const postSeq = ref(Number(route.params.id ?? 0))
const detail = ref<string>('')
const progressBarWidth = ref<Number>(0)
const showNavbar = ref(true)
const lastScrollPosition = ref(0)

const { data, error } = await useAsyncData<IPostReponse>(`post:${postSeq.value}`, async () => {
  const response = await $fetch('/api/board', { params: { id: postSeq.value } })
  if (response.code !== 0) {
    throw createError({ statusCode: response.code, message: response.message, fatal: true })
  }
  return response.data
})

useHead({
  title: `${data.value?.board.title ?? ''} | ${data.value?.business.companyName ?? ''} | 오운드 OUND`,
  meta: [
    { hid: 'og:title', property: 'og:title', content: `${data.value?.board.title ?? ''} | ${data.value?.business.companyName ?? ''} | 오운드 OUND` },
    { hid: 'description', property: 'description', content: data.value?.board.lineSummary },
    { hid: 'og:description', property: 'og:description', content: data.value?.board.lineSummary },
    { hid: 'og:type', property: 'og:type', content: 'article' },
    { hid: 'og:image', property: 'og:image', content: `${data.value?.board.thumbnailImageUrl ?? ''}` }
  ]
})

onMounted(() => {
  const errorData = error.value
  if (errorData) {
    throw createError({ statusCode: errorData.statusCode, message: errorData.message, fatal: true })
  } else {
    post.value = data.value?.board
    business.value = data.value?.business

    init()
  }

  addInteractions()

  if (post.value && business.value) {
    trackingByViewContent({
      postSeq: post.value.postSeq,
      title: post.value.title,
      categoryNo: post.value.categoryNoDepth1,
      categoryNo2: post.value.categoryNoDepth2,
      companyName: business.value.companyName
    })
  }
})
onUnmounted(() => removeInteractions())
onActivated(() => {
  const errorData = error.value
  if (errorData) {
    throw createError({ statusCode: errorData.statusCode, message: errorData.message, fatal: true })
  } else {
    post.value = data.value?.board
    business.value = data.value?.business

    init()
  }
  addInteractions()
})
onDeactivated(() => removeInteractions())

/** 페이지 활성화시 인터렉션 추가 */
const addInteractions = () => {
  window.addEventListener('scroll', handleScroll)
  hideQuickButton()
}
/** 페이지 비활성화시 인터렉션 삭제 */
const removeInteractions = () => {
  window.removeEventListener('scroll', handleScroll)
  showQuickButton()
}

watch(() => data.value, () => {
  post.value = data.value?.board
  business.value = data.value?.business
  init()
})

watch(() => error.value, (errorData: any) => {
  throw createError({ statusCode: errorData.statusCode, message: errorData.message, fatal: true })
})

const init = async () => {
  state.isAuth = await auth.isSignin()
  const renderContainer = document.querySelector('#render_data')
  if (!renderContainer) {
    return
  }
  renderContainer.innerHTML = '<div class="detail_data"></div>'

  const editorContainer = document.querySelector('#render_data .detail_data')
  if (!editorContainer) {
    return
  }
  editorContainer.innerHTML = post.value?.contents ?? ''

  if (!state.isAuth && (post.value?.categoryNoDepth1 === 137623 || post.value?.categoryNoDepth1 === 234793)) {
    removeOverflowingContent(editorContainer, 875 - 100)
  }

  detail.value = editorContainer.innerHTML
  editorContainer.remove()
}

const removeOverflowingContent = (container: any, maxHeight: number) => {
  let totalHeight = 0
  let isRemove = false

  const elements = container.children
  const removeChildNodeList = []
  for (const child of elements) {
    totalHeight += child.clientHeight ?? 0

    if (isRemove) {
      removeChildNodeList.push(child)
      continue
    }

    if (totalHeight > maxHeight) {
      isRemove = true
    }
  }

  for (const child of removeChildNodeList) {
    container.removeChild(child)
  }
}

/**
 * 공유하기
 */
const handleClickShare = async () => {
  if (post.value) {
    const config = useRuntimeConfig()
    const host = config.public.SHOP_HOST as string
    const url = `${host}${PATH.POST_DETAIL}/${post.value.postSeq}?affect_type=${AFFECT_TYPE.SHARE}`

    await share(url, post.value.title ?? '')
    await shareByAnalytics(post.value.postSeq)

    trackingByShareContent()
  }
}

const handleFollow = (item: any) => {
  if (business.value) {
    business.value.followYn = item.followYn

    if (item.followYn === 'Y') {
      business.value.followerCnt = business.value.followerCnt + 1

      if (!(post.value && business.value)) {
        return
      }

      if (!tracking.isFollow && item.followYn === 'Y') {
        tracking.isFollow = true

        trackingByFollowContent({
          postSeq: post.value.postSeq,
          title: post.value.title,
          categoryNo: post.value.categoryNoDepth1,
          categoryNo2: post.value.categoryNoDepth2,
          companyName: business.value.companyName
        })
      }
    } else {
      business.value.followerCnt = business.value.followerCnt - 1
    }
  }
}

const handleUpdateBookmark = (bookmarkData: any) => {
  if (!(post.value && business.value)) {
    return
  }

  if (!tracking.isBookmark && bookmarkData.bookmarkYn === 'Y') {
    tracking.isBookmark = true

    trackingByBookmarkContent({
      postSeq: post.value.postSeq,
      title: post.value.title,
      categoryNo: post.value.categoryNoDepth1,
      categoryNo2: post.value.categoryNoDepth2,
      companyName: business.value.companyName
    })
  }
}

const handleScroll = () => {
  const { scrollTop, scrollHeight, clientHeight } = document.documentElement
  if (scrollTop + clientHeight >= scrollHeight - 600 && !state.isRead) {
    if (state.isAuth && post.value?.postSeq) {
      state.isRead = true
      readByPost(post.value.postSeq)
    }
  }

  updateScrollProgress()
  setGageBar()
}

/** 페이지 스크롤 바 추가 */
const updateScrollProgress = () => {
  const scrollDistance = window.scrollY
  // 전체 스크롤 크기 푸터 제외
  const footerHeight = document.getElementById('footer')?.clientHeight ?? 0
  const totalHeight = document.body.scrollHeight - window.innerHeight - footerHeight
  const scrollProgress = (scrollDistance / totalHeight) * 100
  progressBarWidth.value = scrollProgress
}

const setGageBar = () => {
  const currentScrollPosition = window.scrollY || document.documentElement.scrollTop
  if (currentScrollPosition < 0) {
    return
  }
  if (Math.abs(currentScrollPosition - lastScrollPosition.value) < 60) {
    return
  }
  showNavbar.value = currentScrollPosition < lastScrollPosition.value
  lastScrollPosition.value = currentScrollPosition

  const headerEl = document.querySelector('#header')
  if (!headerEl) {
    return
  }

  if (showNavbar.value) {
    headerEl.classList.remove('gnb_hidden')
  } else {
    headerEl.classList.add('gnb_hidden')
  }
}

const convertDate = (date: number) => {
  return DateUtil.convertDateFormat({
    date: date * 1000,
    format: 'yyyy.MM.dd'
  })
}

/** 카카오 플로팅 버튼 숨김을 위한 detail class 추가 */
const hideQuickButton = () => {
  const headerEl = document.querySelector('#header')
  const footerEl = document.querySelector('#footer')
  if (headerEl) {
    headerEl.classList.add('detail')
  }
  if (footerEl) {
    footerEl.classList.add('detail')
  }
}
const showQuickButton = () => {
  const headerEl = document.querySelector('#header')
  const footerEl = document.querySelector('#footer')
  if (headerEl) {
    headerEl.classList.remove('detail')
  }
  if (footerEl) {
    footerEl.classList.remove('detail')
  }
}

</script>

<style scoped>
.content.sub{position:relative; padding:4px 0 0;}

.detail_top{position:relative; height:268px; overflow:hidden; margin-bottom:40px;}
.detail_top .img{position:relative; height:268px; width:100%;}
.detail_top .img img{position: absolute; top: -100%; right: -100%; bottom: -100%; left: -100%; width: 100%; height: 100%; object-fit: cover; margin: auto;}
.detail_top .text_box{position:absolute; width:100%; height:100%; left:0; top:0; box-sizing:border-box; background:rgba(0, 0, 0, .55); text-align:center; display: flex; flex-direction: column; justify-content: center; align-items: center; gap:24px; padding:0 102px;}
.detail_top .font_p3, .detail_top .font_h3{color:var(--color-background-default);}
.detail_top .font_h3{margin-top:4px;}
.detail_top .text span:before{content:''; display:inline-block; vertical-align:3px; margin:0 5px 0 1px; width:2px; height:2px; background:var(--color-text-sub); border-radius:100%;}

.link_company_follow{position:relative; display: flex; justify-content: space-between; align-items: center;}
.link_company_follow a{display: flex; align-items: center; gap:8px; width:fit-content; width:100%; gap:5px;}
.link_company_follow .logo { position:relative; display:inline-block; width:24px; height:24px; background:#fff; border:1px solid var(--color-line);  border-radius:100%; box-sizing:border-box; overflow:hidden; }
.link_company_follow .logo img{position: absolute; top: -100%; right: -100%; bottom: -100%; left: -100%; width: 100%; height: 100%; margin: auto;}
.link_company_follow .name{display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; white-space: normal; word-break: break-all; max-width:fit-content; font-size:var(--font-size-7); line-height:160%; color:var(--color-background-default); font-weight:normal;}

.detail_data { display: block; font-size: 14px; font-weight: 400; line-height: 1.5; color: #000; font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"; }
.detail_data.logout{height:800px;overflow:hidden;}
/** 개발자 추가 */
.detail_data :deep(img){max-width:100%; height:auto;}

.banner_join .gradient{margin-top: -220px; height: 220px; position: relative; background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 90%); z-index: 1;}
.banner_join .data{text-align:center;padding:100px 0;}
.banner_join .font_h4{color:var(--color-text-headline);}

.banner_tag{display:flex; gap:16px; padding:80px 0 40px; border-bottom:1px solid var(--color-line);}
.banner_tag .font_h4{color:var(--color-text-title); line-height:130%;}
.banner_tag .txt_list{ width: calc(100% + 51px);}
.banner_tag .txt_list ul{display: flex; flex-direction: row; flex-wrap: wrap; gap:8px;}
.banner_tag .txt_list li{padding:2px 0;}
.banner_tag .txt_list li:last-child{margin:0;}
.banner_tag .txt_list li a{display:inline-block; padding:0 8px; line-height:1.375rem; font-size:var(--font-size-8); color:var(--color-text-sub); background:var(--color-background-sub1); box-sizing:border-box; border-radius:4px;}

.banner_company{position:relative; margin:80px 0;}
.banner_company .link_company_text { position:relative; }
.banner_company .link_company_text a{display: flex; gap:24px; align-items: center; position:relative; cursor: default; }
.banner_company .link_company_text .logo{position:relative; display:inline-block; width:78px; height:78px; border-radius:100%; overflow:hidden; flex:none;}
.banner_company .link_company_text .logo span{display:inline-block; width:78px; height:78px; background:#fff; border:1px solid var(--color-line);  border-radius:100%; box-sizing:border-box;}
.banner_company .link_company_text .logo img{position: absolute; top: -100%; right: -100%; bottom: -100%; left: -100%; width: 100%; height: 100%; margin: auto;}
.banner_company .link_company_text .name{display:block; color:var(--color-text-title); letter-spacing:0;}
.banner_company .link_company_text .text{display:block; letter-spacing:0; margin-top:2px;}
.banner_company .link_company_text .text span:before{content:''; display:inline-block; vertical-align:3px; margin:0 5px 0 1px; width:2px; height:2px; background:var(--color-text-sub); border-radius:100%;}
.banner_company .link_company_text .num{display:block; margin-top:8px;}
.banner_company .link_company_text .num span{margin-left:8px;}

.banner_company .btn_box{flex-direction: column; gap:8px; position:absolute; right:0; top:0; width:180px; margin:0;}
.banner_company .btn_box .check_text, .banner_company .btn_box .btn_ty_01{width:100%;}
.banner_company .btn_box + .font_p2{width:560px; margin-top:24px;}

.banner_subscribe{background:var(--color-background-sub1); padding:80px 0;}
.banner_subscribe .inner_box{text-align:right;}
.banner_subscribe .subscribe_box{display:flex; gap:24px; align-items: center; }
.banner_subscribe .subscribe_box + .font_p4{margin-top:16px;}
.banner_subscribe .text{text-align:center;}
.banner_subscribe .font_h4{color:var(--color-text-headline);}
.banner_subscribe .input_area{width:calc(100% - 408px);}
.banner_subscribe .btn_ty_01{width:180px;}

.popup_bookmar{position:fixed; left:0; bottom:0; width:100%; height:60px; z-index: 1; background:var(--color-button-solid-default); display:flex; align-items: center; justify-content: center; gap:8px;}
.popup_bookmar .font_p2{color:var(--color-background-default);}
.popup_bookmar .link_share{margin:0 0 0 17px; display:flex; align-items: center; height:24px;}
.popup_bookmar .link_share:before{content:''; display:inline-block; background:var(--color-icon-gnb); width:1px; height:15px; margin-right:25px;}
.popup_bookmar .icon_font_share:before{color:var(--color-background-default);}

/** 스크롤 네비게이션 */
.gage_bar{position:fixed; left:0; top:60px; width:100%; height:4px; z-index:2;}
.gage_bar span{display:inline-block; height:4px; width:auto; background:var(--color-sub1); vertical-align:top; -webkit-transition: all 0.3s ease;  -moz-transition: all 0.3s ease; -o-transition: all 0.3s ease; transition: all 0.3s ease;}

@media screen and (max-width: 768px) {
  .detail_top{height:212px; width: calc(100% + 40px);  margin-left: -20px;}
  .detail_top .link_company_follow{display:inline-block; position: fixed; top:0; width:calc(100% - 100px); margin:0; height:55px; line-height:55px; left: 50px; z-index: 100; text-align:center; background:var(--color-background-default); display:flex; justify-content: center;}
  .detail_top .text_box{padding:0 20px;}

  .gnb_hidden .detail_top .link_company_follow{transform: translateY(-103%) ; transition: 0s all;}

  .banner_join .gradient{margin-top: -280px;height:280px; margin-bottom: -2px;}

  .link_company_follow a{width:auto;}
  .link_company_follow .logo{width:28px; height:28px;}
  .link_company_follow .name{font-size:var(--font-size-5); color:var(--color-text-title); font-weight:bold; max-width:calc(100% - 33px);}

  .banner_tag{display:block;}
  .banner_tag .txt_list{margin-top:16px; width: calc(100%);}
  .banner_tag .txt_list li{padding: 0;}

  .banner_company .btn_box{position:relative; right:auto; flex-direction: row; width: 100%; margin-top:24px;}
  .banner_company .btn_box .check_text, .banner_company .btn_box .btn_ty_01{width:calc(50% - 6px);}
  .banner_company .btn_box + .font_p2{width:100%;}

  .banner_subscribe .subscribe_box{gap:12px; flex-direction: column;}
  .banner_subscribe .input_area{width:calc(100%);}
  .banner_subscribe .subscribe_box + .font_p4{margin-top:12px; text-align:center;}

  .popup_bookmar{gap:16px; height:40px;}
  .popup_bookmar .font_p2 span{display:none;}
  .popup_bookmar .link_share{height:40px; margin:0;}
  .popup_bookmar .link_share:before{height:40px; margin:0 16px 0 0;}

  .gage_bar{top:56px;}
  .gnb_hidden .gage_bar{top:0; transition: 0s all;}
}
</style>
